import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useLicenseInfo } from '../../../../redux/slices/license/hooks/hooks';
import { useMyTeamRole } from '../../../team/useMyTeamRole';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import type { CampaignDetail } from '../outreachTypes';
import { shortDate } from '../outreachFormat';
import { pitchesUntil, runsUntilOf } from './builderFormat';
import { PLAN_NAMES, planRate } from './planRates';
import { whoMessages as messages } from './whoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Who.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The daily rate of the campaign (design outreach-v2 c1, c2): one card for each plan state. The
 * plan button opens the plan offer with Label selected (`outreachRate`). A member sees the same
 * card; the plan button asks the workspace owner.
 */
const RateCardComponent: React.FC<{ campaign: CampaignDetail }> = ({ campaign }) => {
  const intl = useIntl();
  const { plan } = useMusic24Capabilities();
  const licenseInfo = useLicenseInfo();
  const isOwner = useMyTeamRole() === 'OWNER';
  const openUpgradePrompt = useUpgradePrompt();
  const label = planRate('label');
  const rate = campaign.dailyRate;
  const song = campaign.songTitle;
  const isFree = plan === 'free' || rate === 0;

  const offer = (seePlans: boolean) =>
    isOwner ? (
      <SMLButton
        size="sm"
        color="primary"
        omitCap
        testID="rate-offer"
        onClick={() => openUpgradePrompt(seePlans ? SMLUpgradeFeature.outreach : SMLUpgradeFeature.outreachRate, song)}
      >
        {intl.formatMessage(seePlans ? messages.seePlans : messages.pitchOnLabel, { label })}
      </SMLButton>
    ) : (
      <SMLButton size="sm" outline omitCap disabled testID="rate-ask-owner">
        {intl.formatMessage(messages.askOwner)}
      </SMLButton>
    );

  const content = (): { title: string; body: string; action: React.ReactNode } => {
    if (isFree) {
      return {
        title: intl.formatMessage(messages.rateFreeTitle, { count: campaign.poolTotal, song }),
        body: intl.formatMessage(messages.rateFreeBody, { pro: planRate('pro'), label }),
        action: offer(true),
      };
    }
    if (plan === 'trial') {
      const endsAt = licenseInfo.trialEndsAt;
      return {
        title: intl.formatMessage(messages.rateTrialTitle, { rate }),
        body: intl.formatMessage(messages.rateTrialBody, { artist: planRate('artist'), pro: planRate('pro'), label }),
        action: (
          <>
            {offer(false)}
            {endsAt !== null && (
              <span className={shared.small}>
                {intl.formatMessage(messages.trialEnds, { date: shortDate(intl, endsAt.toISOString()) })}
              </span>
            )}
          </>
        ),
      };
    }
    if (plan === 'label') {
      return {
        title: intl.formatMessage(messages.rateTitle, { rate, song }),
        body: intl.formatMessage(messages.rateLabelBody, { rate: planRate('label') }),
        action: (
          <span className={`${shared.chip} ${shared.chipVio}`}>
            {intl.formatMessage(messages.yourPlan, { plan: intl.formatMessage(PLAN_NAMES.label) })}
          </span>
        ),
      };
    }
    const now = new Date();
    const endsAt = runsUntilOf(campaign, now);
    const total = pitchesUntil(endsAt, rate, now);
    return {
      title: intl.formatMessage(messages.rateTitle, { rate, song }),
      body:
        plan === 'manager'
          ? intl.formatMessage(messages.rateManagerBody, { rate: planRate('manager') })
          : endsAt === null || total === null
            ? intl.formatMessage(messages.rateBody, { rate })
            : intl.formatMessage(messages.rateBodyUntil, {
                rate,
                total: intl.formatNumber(total),
                date: shortDate(intl, endsAt.toISOString()),
              }),
      action: (
        <>
          {offer(false)}
          <span className={shared.small}>
            {intl.formatMessage(messages.yourPlan, { plan: intl.formatMessage(PLAN_NAMES[plan]) })}
          </span>
        </>
      ),
    };
  };

  const { title, body, action } = content();
  return (
    <>
      <div className={styles.rate} data-testid="rate-card">
        <div className={styles.rateNumber}>
          {isFree ? 0 : rate}
          <small>{intl.formatMessage(messages.aDay)}</small>
        </div>
        <div className={styles.rateText}>
          <b>{title}</b>
          <span>{body}</span>
        </div>
        <div className={styles.rateAction}>{action}</div>
      </div>
      {isFree && <p className={shared.small}>{intl.formatMessage(messages.rateFreeNote)}</p>}
    </>
  );
};

export const RateCard = TypedMemo(RateCardComponent);
